import type { FC } from "react";
import { useState } from "react";
import { useForm } from "@tanstack/react-form";
import { useNavigate } from "react-router-dom";

import { useGetConsultationsFromPatient } from "@/api/consultations";
import { useGetPatientPerioquiz, usePatchPatient } from "@/api/patients";
import Edit from "@/assets/icons/edit";
import HelpSquare from "@/assets/icons/help-square";
import Upload from "@/assets/icons/upload";
import { FieldSelect } from "@/components/form";
import GenderIcon from "@/components/layout/gender-icon";
import DiseaseUpdateModal from "@/components/modals/disease-update-modal";
import ExportModal from "@/components/modals/export/export-modal";
import InvitePatientModal from "@/components/modals/invite-patient-modal";
import PatchPatientCommentModal from "@/components/modals/patch-patient-comment-modal";
import PatchPatientConsultationMotiveCommentModal from "@/components/modals/patch-patient-consultation-motive-comment-modal";
import PatchPatientStateGradeAbscessModal from "@/components/modals/patch-patient-grade-modal";
import PerioquizListSideModal from "@/components/modals/perioquiz/perioquiz-list-side-modal";
import PerioquizSideModal from "@/components/modals/perioquiz/perioquiz-side-modal";
import PatientAdministrativeInfos from "@/components/patient/patient-administrative-infos";
import PatientGeneralHealth from "@/components/patient/patient-general-health";
import PatientHygiene from "@/components/patient/patient-hygiene";
import PatientRiskFactors from "@/components/patient/patient-risk-factors";
import RiskFactorCell from "@/components/table/patients/risk-factor-cell";
import { Button } from "@/components/ui/button";
import { patchProfileStateSchema } from "@/schemas/patient";
import type { Patient } from "@/types/api/patient";
import type { Perioquiz } from "@/types/api/perioquiz";
import { resetForm } from "@/utils/form";
import {
  consultationOptions,
  getAge,
  getConsultationLabel,
  getDiseaseLabel,
  getName,
  stateOptions,
} from "@/utils/patient";

export type PatientProps = {
  patient: Patient;
};

const PatientProfile: FC<PatientProps> = ({ patient }) => {
  const navigate = useNavigate();
  const { mutate } = usePatchPatient(patient.id);
  const { data: consultations = [], isLoading: isConsultationsLoading } =
    useGetConsultationsFromPatient(patient.id);
  const { data: perioquiz = [] } = useGetPatientPerioquiz(patient.id);

  const [isDiseaseModalOpen, setIsDiseaseModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isPatchPatientCommentModalOpen, setIsPatchPatientCommentModalOpen] =
    useState(false);
  const [isPerioquizListOpen, setIsPerioquizListOpen] = useState(false);
  const [
    isPatchStateGradeAbscessModalOpen,
    setIsPatchStateGradeAbscessModalOpen,
  ] = useState(false);
  const [perioquizModalOpen, setPerioquizModalOpen] =
    useState<Perioquiz | null>(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [
    isPatchPatientConsultationMotiveCommentModalOpen,
    setIsPatchPatientConsultationMotiveCommentModalOpen,
  ] = useState(false);

  const { Field, handleSubmit } = useForm({
    defaultValues: {
      consultationMotive: patient.consultationMotive,
      diseaseState: patient.diseaseState,
    },
    onSubmit: async ({ value, formApi }) => {
      const values = patchProfileStateSchema.parse(value);
      mutate(value, {
        onSuccess: () => {
          resetForm(values, formApi);
        },
      });
    },
  });

  return (
    <div className="flex flex-col max-w-[500px] w-full px-14 py-8 gap-12 bg-white border-r border-gray-200">
      <div className="flex flex-col gap-6">
        <div className="relative flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <h2 className="font-semibold text-[32px] text-gray-950">
              {getName(patient)}
            </h2>
            <div
              className="h-9 w-9 bg-gray items-center justify-center cursor-pointer p-2 border border-gray-300 bg-gray-50 rounded-3xl shadow"
              onClick={() => setIsPerioquizListOpen(true)}
            >
              {perioquiz.length > 0 &&
              new Date(perioquiz[0].createdAt) >
                new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) ? (
                <div className="h-2 w-2 bg-[#E53935] rounded-full absolute top-3 right-1 shadow-md" />
              ) : null}
              <HelpSquare
                className="w-5 h-5"
                color={
                  perioquiz.length > 0 &&
                  new Date(perioquiz[0].createdAt) >
                    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
                    ? "#B91C1C"
                    : undefined
                }
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <FieldSelect
                Field={Field}
                schema={patchProfileStateSchema}
                options={stateOptions}
                name="diseaseState"
                onValueChange={() => handleSubmit()}
                risk="tooth"
                disease={getDiseaseLabel(patient.disease)}
              />
              <div className="flex flex-col items-start pt-3">
                <Button
                  type="button"
                  variant={"text"}
                  size={"none"}
                  style={{ height: "fit-content" }}
                  onClick={() => setIsDiseaseModalOpen(true)}
                >
                  <Edit />
                </Button>
              </div>
              <div className="flex text-xs leading-6 items-center">
                {patient.stateGradeAbscess
                  ? patient.stateGradeAbscess
                  : "Stade grade abcès"}
              </div>
              <Button
                type="button"
                variant={"text"}
                size={"none"}
                onClick={() => setIsPatchStateGradeAbscessModalOpen(true)}
              >
                <Edit />
              </Button>
            </div>
            <div className="flex gap-2">
              <GenderIcon gender={patient.gender} />
              <div className="font-semibold text-lg leading-4">.</div>
              <div className="text-xs leading-6">{`${getAge(patient.birthDate)} ans`}</div>
              <div className="font-semibold text-lg leading-4">.</div>
              {patient.patientComment ? (
                <div className="text-xs leading-6">
                  {patient.patientComment}
                </div>
              ) : (
                <div className="text-xs leading-6 text-gray-500">Compliant</div>
              )}
              <Button
                type="button"
                variant={"text"}
                size={"none"}
                onClick={() => setIsPatchPatientCommentModalOpen(true)}
              >
                <Edit />
              </Button>
            </div>
            <RiskFactorCell patient={patient} isProfile={true} />
          </div>
          <FieldSelect
            Field={Field}
            schema={patchProfileStateSchema}
            options={consultationOptions}
            name="consultationMotive"
            className="bg-gray-50 font-normal justify-start gap-2"
            onValueChange={() => handleSubmit()}
            isMotive
          >
            <div className="flex flex-1">
              {getConsultationLabel(patient.consultationMotive)}
            </div>
          </FieldSelect>
          <div className="flex gap-2">
            {patient.consultationMotiveComment ? (
              <div className="flex items-center text-xs leading-6">
                {patient.consultationMotiveComment}
              </div>
            ) : (
              <div className="flex items-center text-xs leading-6 text-gray-500">
                Commentaire sur le motif de la consultation
              </div>
            )}
            <Button
              type="button"
              variant={"text"}
              size={"none"}
              onClick={() =>
                setIsPatchPatientConsultationMotiveCommentModalOpen(true)
              }
            >
              <Edit />
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            type="button"
            variant={"blue"}
            className="whitespace-nowrap"
            onClick={() => setIsExportModalOpen(true)}
            loading={isConsultationsLoading}
          >
            <Upload />
            {"Générer un compte-rendu"}
          </Button>
          <Button
            type="button"
            variant={"outline"}
            className="whitespace-nowrap"
            onClick={() =>
              navigate("/patients/fiche", { state: { patientId: patient.id } })
            }
          >
            <Edit color="#64748B" />
            {"Modifier les informations"}
          </Button>
          {patient.code !== "USED" ? (
            <Button
              type="button"
              variant={"outline"}
              className="whitespace-nowrap"
              onClick={() => setIsInviteModalOpen(true)}
            >
              {"Inviter sur MyPerioApp"}
            </Button>
          ) : null}
        </div>
        <PatientRiskFactors patient={patient} />
        <PatientGeneralHealth patient={patient} />
        <PatientHygiene patient={patient} />
        <PatientAdministrativeInfos patient={patient} />
      </div>
      <DiseaseUpdateModal
        open={isDiseaseModalOpen}
        setOpen={setIsDiseaseModalOpen}
        patient={patient}
      />
      <InvitePatientModal
        open={isInviteModalOpen}
        setOpen={setIsInviteModalOpen}
        patient={patient}
      />
      <PatchPatientCommentModal
        patient={patient}
        isOpen={isPatchPatientCommentModalOpen}
        setIsOpen={setIsPatchPatientCommentModalOpen}
      />
      <PatchPatientStateGradeAbscessModal
        patient={patient}
        isOpen={isPatchStateGradeAbscessModalOpen}
        setIsOpen={setIsPatchStateGradeAbscessModalOpen}
      />
      <PatchPatientConsultationMotiveCommentModal
        patient={patient}
        isOpen={isPatchPatientConsultationMotiveCommentModalOpen}
        setIsOpen={setIsPatchPatientConsultationMotiveCommentModalOpen}
      />
      <PerioquizListSideModal
        patient={patient}
        open={isPerioquizListOpen}
        onOpenChange={setIsPerioquizListOpen}
        onSelect={(selected: Perioquiz) => {
          setPerioquizModalOpen(selected);
          setIsPerioquizListOpen(false);
        }}
      />
      <PerioquizSideModal
        perioquiz={perioquizModalOpen}
        open={perioquizModalOpen !== null}
        onOpenChange={() => {
          setPerioquizModalOpen(null);
          setIsPerioquizListOpen(true);
        }}
      />
      <ExportModal
        patient={patient}
        consultations={consultations}
        open={isExportModalOpen}
        onOpenChange={setIsExportModalOpen}
      />
    </div>
  );
};

export default PatientProfile;
